import React, { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Play } from "lucide-react";

// Stop the moving marker inside the target zone.
export function Bullseye({ onScore }) {
  const [pos, setPos] = useState(0);
  const [running, setRunning] = useState(false);
  const [over, setOver] = useState(false);
  const [score, setScore] = useState(0);
  const [round, setRound] = useState(0);
  const [zone, setZone] = useState({ start: 40, width: 20 });
  const dir = useRef(1);
  const raf = useRef(null);
  const speed = useRef(1.2);
  const reported = useRef(false);

  const start = () => { setScore(0); setRound(1); setOver(false); setRunning(true); speed.current = 1.2; newZone(); reported.current = false; };
  const newZone = () => { const w = Math.max(10, 22 - round * 1.5); setZone({ start: 10 + Math.random() * (80 - w), width: w }); };

  useEffect(() => {
    if (!running) return;
    const loop = () => {
      setPos((p) => { let np = p + dir.current * speed.current; if (np >= 100) { np = 100; dir.current = -1; } if (np <= 0) { np = 0; dir.current = 1; } return np; });
      raf.current = requestAnimationFrame(loop);
    };
    raf.current = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf.current);
  }, [running]);

  const stop = () => {
    if (!running) return;
    const hit = pos >= zone.start && pos <= zone.start + zone.width;
    if (hit) { setScore((s) => s + 1); speed.current += 0.4; if (round >= 8) finish(score + 1); else { setRound((r) => r + 1); newZone(); } }
    else finish(score);
  };

  const finish = (final) => { setRunning(false); setOver(true); if (!reported.current) { reported.current = true; onScore?.(final * 150); } };

  return (
    <div className="flex flex-col items-center w-full max-w-md text-center" data-testid="bullseye-game">
      <div className="flex gap-6 mb-4 font-mono">
        <span className="text-neon-cyan text-lg" data-testid="bullseye-score">SCORE: {score}</span>
        <span className="text-neon-yellow">ROUND: {round}</span>
      </div>
      <div className="relative w-full h-12 bg-zinc-800 rounded-full overflow-hidden border-2 border-zinc-700 mb-6">
        {running && <div className="absolute top-0 bottom-0 bg-neon-lime/40" style={{ left: `${zone.start}%`, width: `${zone.width}%` }} />}
        {running && <div className="absolute top-0 bottom-0 w-1.5 bg-neon-pink" data-testid="bullseye-marker" style={{ left: `${pos}%`, boxShadow: "0 0 10px #FF003C" }} />}
      </div>
      {!running ? (
        <>
          {over && <p className="font-display font-black text-2xl text-neon-lime uppercase mb-3" data-testid="bullseye-over">Score: {score}</p>}
          <Button onClick={start} data-testid="bullseye-start" className="bg-neon-cyan text-black rounded-none font-bold uppercase h-12 px-8">
            <Play className="w-4 h-4 mr-2 fill-black" /> {over ? "Play Again" : "Start"}
          </Button>
        </>
      ) : (
        <Button onClick={stop} data-testid="bullseye-stop" className="bg-neon-pink text-white rounded-none font-bold uppercase h-14 px-12 text-lg">STOP</Button>
      )}
      <p className="text-zinc-500 text-xs font-mono mt-4">Stop the marker inside the green zone. It shrinks and speeds up each round!</p>
    </div>
  );
}
